import React, { useContext, useMemo, useState } from "react";
import { CartContext } from "./CartContext";
import "../css/checkout.css";
import productData from "../Datas/productData";
import { Link } from "react-router-dom";
import siteConfig, { formatMoney } from "../siteConfig";
import BrandMark from "./BrandMark";

const createOrderNumber = () => {
  const stamp = Date.now().toString(36).toUpperCase();
  if (window.crypto?.randomUUID) return `HIB-${stamp}-${window.crypto.randomUUID().slice(0, 8).toUpperCase()}`;
  const bytes = new Uint32Array(2);
  window.crypto?.getRandomValues?.(bytes);
  const random = bytes[0] ? `${bytes[0].toString(36)}${bytes[1].toString(36)}` : Math.random().toString(36).slice(2, 12);
  return `HIB-${stamp}-${random.toUpperCase()}`;
};

const initialForm = {
  email: "",
  country: siteConfig.operatingRegion,
};

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const lines = useMemo(() => cartItems
    .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
    .filter((item) => item.product), [cartItems]);
  const subtotal = lines.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const total = Number((subtotal + siteConfig.shippingFee).toFixed(2));

  const update = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  const placeOrder = (event) => {
    event.preventDefault();
    setError("");
    if (!lines.length) return;
    const requiredValues = [form.email, form.country];
    if (requiredValues.some((value) => !value.trim())) {
      setError("Complete the required contact and delivery fields.");
      return;
    }
    if (!event.currentTarget.checkValidity()) {
      event.currentTarget.reportValidity();
      return;
    }

    setSubmitting(true);
    const origin = window.location.origin;
    const params = new URLSearchParams({
      orderNo: createOrderNumber(),
      amount: total.toFixed(2),
      currency: siteConfig.currency,
      email: form.email.trim(),
      country: form.country,
      success_url: new URL("/payment?state=success", origin).href,
      cancel_url: new URL("/payment?state=cancel", origin).href,
      title: lines[0].product.name,
    });
    window.location.assign(`${siteConfig.payment.endpoint}?${params.toString()}`);
  };

  if (!lines.length) return <main className="checkout-empty page-shell"><h1>Your cart is empty</h1><p>Add an item before starting hosted checkout.</p><Link className="button-link" to="/shop">Return to shop</Link></main>;

  return (
    <main className="checkout-page">
      <div className="checkout-brand"><Link to="/" className="checkout-wordmark"><BrandMark /></Link><nav><Link to="/cartpage">Cart</Link><span>›</span><strong>Checkout</strong></nav></div>
      <div className="checkout-layout">
        <form onSubmit={placeOrder} noValidate>
          <section><h1>Checkout</h1><p className="secure-note">Choose the destination here. The hosted payment page collects the full delivery address and card information.</p></section>
          <section><h2>Contact</h2><label>Email<input required type="email" name="email" autoComplete="email" value={form.email} onChange={update} /></label></section>
          <section><h2>Delivery destination</h2>
            <label>Country / Region<select required name="country" value={form.country} onChange={update}>{siteConfig.checkoutCountries.map((country) => <option key={country}>{country}</option>)}</select></label>
            <p className="secure-note">Dispatch: {siteConfig.fulfilment.dispatchTime}. The carrier and tracking number are emailed after hand-off.</p>
          </section>
          <section className="payment-handoff"><h2>Hosted payment</h2><p><strong>No delivery address or card fields are stored on this page.</strong> Pay Now opens the payment session created by the same-origin PHP endpoint. Returning to this site does not by itself confirm settlement.</p><p>By continuing, you agree to enter an accurate delivery address on the hosted page and confirm that you have reviewed the <Link to="/terms">Terms</Link>, <Link to="/privacy">Privacy</Link> and <Link to="/shipping">Shipping</Link> policies.</p></section>
          {error && <p className="checkout-error" role="alert">{error}</p>}
          <button className="pay-button" disabled={submitting}>{submitting ? "Opening secure checkout…" : `Pay Now · ${formatMoney(total)}`}</button>
        </form>
        <aside className="checkout-summary">
          {lines.map((item) => <div className="checkout-line" key={`${item.id}-${item.size}-${item.color}`}><div><img src={item.product.image} alt="" /><span>{item.quantity}</span></div><p>{item.product.name}<small>{item.size} / {item.color}</small></p><strong>{formatMoney(item.product.price * item.quantity)}</strong></div>)}
          <div className="checkout-totals"><p><span>Subtotal</span><strong>{formatMoney(subtotal)}</strong></p><p><span>Shipping</span><strong>{formatMoney(siteConfig.shippingFee)}</strong></p><p className="total"><span>Total <small>{siteConfig.currency}</small></span><strong>{formatMoney(total)}</strong></p></div>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
